import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Container,
  Paper,
  Text,
  Progress,
  Button,
  Group,
  Stack,
  Radio,
  Checkbox,
  Divider,
  Alert,
  Card,
  Badge,
  ActionIcon,
  Modal,
  Box,
  rem,
  Tooltip,
  SegmentedControl,
  RingProgress,
} from "@mantine/core";
import { Grid2 as Grid } from "@mui/material";
import {
  IconAlertCircle,
  IconChevronLeft,
  IconChevronRight,
  IconCheck,
  IconX,
  IconBookmark,
  IconBookmarkFilled,
  IconArrowBack,
  IconRefresh,
  IconTrophy,
} from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";

import { Colors, HttpSuccessCodesObj } from "../../Lib/data";
import { Endpoints } from "../../Lib/endpoints";
import {
  AssessmentProps,
  AttemptProps,
  QuestionProps,
  QuestionOptionProps,
} from "../../Lib/types/assessment";
import { PerformRequest, usePerformRequest } from "../../Lib/usePerformRequest";
import { NonPaginatedResponse } from "../../Lib/responses";
import MegaLoader from "../../Components/MegaLoader";

import "./styles.scss";
import { DefaultErrorNotification, DefaultSuccessNotification } from "../../Lib/methods";

const SingleAssessment: React.FC = () => {
  const { assessmentID } = useParams();
  const navigate = useNavigate();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string[]>>({});
  const [bookmarkedQuestions, setBookmarkedQuestions] = useState<Set<number>>(new Set());
  const [isSubmitting, setSubmitting] = useState(false);
  const [opened, { open, close }] = useDisclosure(false);
  const [navigationView, setNavigationView] = useState<"all" | "bookmarked">("all");
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState<number | null>(null);
  const [showExitConfirm, setShowExitConfirm] = useState(false);

  const {
    data: assessment,
    isLoading,
    reloadData: reloadAssessment,
  } = usePerformRequest<
    NonPaginatedResponse<{
      assessment: AssessmentProps;
      attempts: AttemptProps[];
    }>
  >({
    route: Endpoints.GetSingleAssessmentDetails(assessmentID as string).route,
    method: Endpoints.GetSingleAssessmentDetails(assessmentID as string).method,
  });

  const currentQuestion = assessment?.data?.assessment.questions[currentQuestionIndex];
  const totalQuestions = assessment?.data?.assessment.questions.length || 0;
  const progress = ((currentQuestionIndex + 1) / totalQuestions) * 100;

  const handleAnswerChange = (questionId: string, value: string | string[]) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: Array.isArray(value) ? value : [value],
    }));
  };

  const handleNext = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    const entries = Object.entries(answers);
    const a = entries.map(([key, value]) => ({
      questionID: key,
      answers: value,
    }));

    const r = await PerformRequest<NonPaginatedResponse<{ score: number }>>({
      method: Endpoints.GetSubmitAssessment(assessmentID as string).method,
      route: Endpoints.GetSubmitAssessment(assessmentID as string).route,
      data: {
        answers: a,
      },
      callback: () => {
        setSubmitting(false);
      },
    });

    setSubmitting(false);
    if (r && r.status) {
      if (r.status === HttpSuccessCodesObj.Created) {
        DefaultSuccessNotification("Assessment Submitted Successfully");
        // Show results modal with the score from the response
        setScore(r.data?.data?.score || 0);
        setShowResults(true);
        close(); // Close the submit modal
      } else {
        DefaultErrorNotification("Error Submitting Assessment");
      }
    } else {
      DefaultErrorNotification("Error Submitting Assessment");
    }
  };

  const handleRetake = () => {
    // Reset all state
    setCurrentQuestionIndex(0);
    setAnswers({});
    setBookmarkedQuestions(new Set());
    setShowResults(false);
    setScore(null);
    // Reload assessment data
    reloadAssessment();
  };

  const toggleBookmark = (questionIndex: number) => {
    setBookmarkedQuestions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(questionIndex)) {
        newSet.delete(questionIndex);
      } else {
        newSet.add(questionIndex);
      }
      return newSet;
    });
  };

  const isQuestionAnswered = (questionId: string) => {
    const answer = answers[questionId];
    return answer && answer.length > 0;
  };

  const allQuestionsAnswered = assessment?.data?.assessment.questions.every((q: QuestionProps) =>
    isQuestionAnswered(q.id)
  );

  const filteredQuestions = assessment?.data?.assessment.questions.filter(
    (_: QuestionProps, index: number) => navigationView === "all" || bookmarkedQuestions.has(index)
  );

  // GRADE CALCULATION FUNCTION
  function getGrade(score: number | null) {
    if (score === null) return "-";
    if (score >= 75) return "A";
    if (score >= 65) return "B";
    if (score >= 55) return "C";
    if (score >= 45) return "D";
    return "F";
  }

  // GRADE GRADIENT FUNCTION
  function getGradeGradient(score: number | null) {
    const grade = getGrade(score);
    if (grade === "A")
      return "linear-gradient(135deg, var(--mantine-color-green-6) 0%, var(--mantine-color-teal-6) 100%)";
    if (grade === "B")
      return "linear-gradient(135deg, var(--mantine-color-blue-6) 0%, var(--mantine-color-cyan-6) 100%)";
    if (grade === "C")
      return "linear-gradient(135deg, var(--mantine-color-yellow-4) 0%, var(--mantine-color-yellow-6) 100%)";
    if (grade === "D")
      return "linear-gradient(135deg, var(--mantine-color-orange-5) 0%, var(--mantine-color-orange-7) 100%)";
    if (grade === "F")
      return "linear-gradient(135deg, var(--mantine-color-red-6) 0%, var(--mantine-color-pink-6) 100%)";
    return "linear-gradient(135deg, var(--mantine-color-gray-5) 0%, var(--mantine-color-gray-7) 100%)";
  }

  if (isLoading) {
    return <MegaLoader />;
  }

  return (
    <Container size="lg" className="single-assessment-container">
      <Group align="flex-start" gap="xl">
        <Box style={{ flex: 1 }}>
          <Paper className="assessment-header" p="md" w={500} radius="md" withBorder>
            <Group justify="space-between" mb="md">
              <div>
                <Group gap="xs">
                  <Text fw={700} fz={24} c={Colors.PurplePrimary}>
                    {assessment?.data?.assessment.title}
                  </Text>
                  <Button
                    variant="subtle"
                    color={Colors.RedPrimary}
                    size="xs"
                    leftSection={<IconArrowBack size={14} />}
                    onClick={() => setShowExitConfirm(true)}
                  >
                    Exit Assessment
                  </Button>
                </Group>
                <Group gap="xs" mt={4}>
                  <Text size="sm" c="dimmed">
                    {totalQuestions} Questions
                  </Text>
                  <Text size="sm" c="dimmed">
                    •
                  </Text>
                  <Text size="sm" c="dimmed">
                    {assessment?.data?.attempts.length || 0} Previous Attempts
                  </Text>
                </Group>
              </div>
              <Badge
                size="lg"
                variant="light"
                color={assessment?.data?.assessment.isActive ? "green" : "red"}
              >
                {assessment?.data?.assessment.isActive ? "Active" : "Inactive"}
              </Badge>
            </Group>
            <Progress value={progress} size="sm" color={Colors.PurplePrimary} radius="xl" />
          </Paper>

          <Card className="question-card" mt="md" p="xl" radius="md" withBorder>
            <Group justify="space-between" mb="xl">
              <Group>
                <Text fw={600} fz="lg">
                  Question {currentQuestionIndex + 1} of {totalQuestions}
                </Text>
                {bookmarkedQuestions.has(currentQuestionIndex) && (
                  <IconBookmarkFilled
                    size={16}
                    color={Colors.PurplePrimary}
                    style={{ marginLeft: 8 }}
                  />
                )}
              </Group>
              <Group>
                <Tooltip
                  label={
                    bookmarkedQuestions.has(currentQuestionIndex)
                      ? "Remove Bookmark"
                      : "Bookmark Question"
                  }
                >
                  <ActionIcon
                    variant="light"
                    color={bookmarkedQuestions.has(currentQuestionIndex) ? "grape" : "gray"}
                    onClick={() => toggleBookmark(currentQuestionIndex)}
                  >
                    {bookmarkedQuestions.has(currentQuestionIndex) ? (
                      <IconBookmarkFilled size={16} />
                    ) : (
                      <IconBookmark size={16} />
                    )}
                  </ActionIcon>
                </Tooltip>
                <Badge size="md" variant="light" color="blue">
                  {currentQuestion?.type === "single" ? "Single Choice" : "Multiple Choice"}
                </Badge>
              </Group>
            </Group>

            <Text fw={500} fz="md" mb="xl" maw={400}>
              {currentQuestion?.title}
            </Text>

            <Stack gap="md">
              {currentQuestion?.options.map((option: QuestionOptionProps) => (
                <Paper
                  key={option.value}
                  p="md"
                  radius="md"
                  withBorder
                  className={`option-paper ${
                    answers[currentQuestion.id]?.includes(option.value) ? "selected" : ""
                  }`}
                  onClick={() => {
                    if (currentQuestion.type === "single") {
                      handleAnswerChange(currentQuestion.id, option.value);
                    } else {
                      const currentAnswers = answers[currentQuestion.id] || [];
                      const newAnswers = currentAnswers.includes(option.value)
                        ? currentAnswers.filter((v) => v !== option.value)
                        : [...currentAnswers, option.value];
                      handleAnswerChange(currentQuestion.id, newAnswers);
                    }
                  }}
                >
                  <Group justify="space-between">
                    <Text fz="sm">{option.label}</Text>
                    {currentQuestion.type === "single" ? (
                      <Radio
                        checked={answers[currentQuestion.id]?.includes(option.value)}
                        size="xs"
                        onChange={() => {}}
                        value={option.value}
                      />
                    ) : (
                      <Checkbox
                        checked={answers[currentQuestion.id]?.includes(option.value)}
                        onChange={() => {}}
                      />
                    )}
                  </Group>
                </Paper>
              ))}
            </Stack>

            <Group justify="space-between" mt="xl">
              <Button
                variant="light"
                leftSection={<IconChevronLeft size={16} />}
                onClick={handlePrevious}
                disabled={currentQuestionIndex === 0}
              >
                Previous
              </Button>
              {currentQuestionIndex === totalQuestions - 1 ? (
                <Button
                  color="green"
                  rightSection={<IconCheck size={16} />}
                  onClick={open}
                  loading={isSubmitting}
                  disabled={!allQuestionsAnswered}
                >
                  Submit Assessment
                </Button>
              ) : (
                <Button
                  variant="filled"
                  rightSection={<IconChevronRight size={16} />}
                  onClick={handleNext}
                  color={Colors.PurplePrimary}
                >
                  Next
                </Button>
              )}
            </Group>
          </Card>
        </Box>

        <Paper p="md" radius="md" withBorder w={300} style={{ position: "sticky", top: 20 }}>
          <Stack gap="md">
            <Group justify="space-between" align="center">
              <Text fw={600}>Question Navigation</Text>
              <SegmentedControl
                value={navigationView}
                onChange={(value) => setNavigationView(value as "all" | "bookmarked")}
                w="100%"
                data={[
                  { label: "All", value: "all" },
                  { label: "Bookmarked", value: "bookmarked" },
                ]}
                size="sm"
              />
            </Group>

            <Grid container spacing={1}>
              {filteredQuestions?.map((question: QuestionProps, index: number) => {
                const originalIndex =
                  assessment?.data?.assessment.questions.findIndex(
                    (q: QuestionProps) => q.id === question.id
                  ) ?? 0;
                return (
                  <Grid key={question.id} sx={{ width: "20.33%" }}>
                    <Paper
                      w={45}
                      h={45}
                      radius="md"
                      withBorder
                      className="flex-row align-center justify-center"
                      style={{
                        cursor: "pointer",
                        backgroundColor:
                          currentQuestionIndex === originalIndex
                            ? "var(--mantine-color-blue-0)"
                            : isQuestionAnswered(question.id)
                            ? "var(--mantine-color-green-0)"
                            : undefined,
                        borderColor:
                          currentQuestionIndex === originalIndex
                            ? "var(--mantine-color-blue-3)"
                            : undefined,
                        textAlign: "center",
                      }}
                      onClick={() => setCurrentQuestionIndex(originalIndex)}
                    >
                      <Group justify="center" gap={4}>
                        <Text size="sm">{originalIndex + 1}</Text>
                        {bookmarkedQuestions.has(originalIndex) && (
                          <IconBookmarkFilled size={12} color={Colors.PurplePrimary} />
                        )}
                      </Group>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>

            <Divider />

            <Stack gap="xs">
              <Group justify="space-between">
                <Text size="sm" c="dimmed">
                  Answered
                </Text>
                <Text size="sm" fw={500}>
                  {Object.keys(answers).length} of {totalQuestions}
                </Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">
                  Bookmarked
                </Text>
                <Text size="sm" fw={500}>
                  {bookmarkedQuestions.size}
                </Text>
              </Group>
            </Stack>
          </Stack>
        </Paper>
      </Group>

      <Modal
        opened={opened}
        onClose={close}
        centered
        size="md"
        withCloseButton={false}
        padding={0}
        radius="md"
      >
        <Box>
          <Paper
            p="xl"
            radius="md"
            style={{
              background:
                "linear-gradient(135deg, var(--mantine-color-blue-6) 0%, var(--mantine-color-indigo-6) 100%)",
            }}
          >
            <Stack align="center" gap="xs">
              <IconCheck
                size={48}
                color="white"
                style={{ filter: "drop-shadow(0 0 8px rgba(255,255,255,0.5))" }}
              />
              <Text fw={700} fz={24} c="white" ta="center">
                Ready to Submit?
              </Text>
              <Text c="white" ta="center" size="sm" opacity={0.9}>
                Please review your answers before submitting
              </Text>
            </Stack>
          </Paper>

          <Stack p="xl" gap="xl">
            <Group grow>
              <Paper p="md" radius="md" withBorder>
                <Stack gap="xs" align="center">
                  <Text size="sm" c="dimmed">
                    Questions Answered
                  </Text>
                  <Group gap={4}>
                    <Text fw={700} fz={24} c={Colors.PurplePrimary}>
                      {Object.keys(answers).length}
                    </Text>
                    <Text size="sm" c="dimmed">
                      / {totalQuestions}
                    </Text>
                  </Group>
                </Stack>
              </Paper>

              <Paper p="md" radius="md" withBorder>
                <Stack gap="xs" align="center">
                  <Text size="sm" c="dimmed">
                    Bookmarked
                  </Text>
                  <Text fw={700} fz={24} c={Colors.PurplePrimary}>
                    {bookmarkedQuestions.size}
                  </Text>
                </Stack>
              </Paper>
            </Group>

            {!allQuestionsAnswered && (
              <Alert
                icon={<IconX size={16} />}
                title="Incomplete Assessment"
                color="red"
                variant="light"
                radius="md"
              >
                <Text size="sm">
                  You have {totalQuestions - Object.keys(answers).length} unanswered questions.
                  Please complete all questions before submitting.
                </Text>
              </Alert>
            )}

            <div className="flex-row align-center justify-between width-100">
              <Button
                w="48%"
                variant="light"
                color="gray"
                onClick={close}
                size="sm"
                rightSection={<IconArrowBack size={16} />}
              >
                Review Answers
              </Button>

              <Button
                w="48%"
                color="blue"
                onClick={handleSubmit}
                loading={isSubmitting}
                rightSection={<IconCheck size={16} />}
                disabled={!allQuestionsAnswered}
                size="sm"
              >
                Submit
              </Button>
            </div>
          </Stack>
        </Box>
      </Modal>

      <Modal
        opened={showResults}
        onClose={() => {}}
        centered
        size="md"
        withCloseButton={false}
        padding={0}
        radius="md"
        closeOnClickOutside={false}
        closeOnEscape={false}
      >
        <Box>
          <Paper
            p="xl"
            radius="md"
            style={{
              background: getGradeGradient(score),
            }}
          >
            <Stack align="center" gap="xs">
              <IconTrophy
                size={48}
                color="white"
                style={{ filter: "drop-shadow(0 0 8px rgba(255,255,255,0.5))" }}
              />
              <Text fw={700} fz={24} c="white" ta="center">
                Assessment Complete!
              </Text>
              <Text c="white" ta="center" size="sm" opacity={0.9}>
                Great job completing the assessment
              </Text>
            </Stack>
          </Paper>

          <Stack p="xl" gap="xl">
            <Group justify="center">
              <RingProgress
                size={180}
                thickness={16}
                roundCaps
                sections={[{ value: score || 0, color: "green" }]}
                label={
                  <Text ta="center" size="xl" fw={700} c="green">
                    {score}%
                  </Text>
                }
              />
            </Group>
            <Stack gap={0} align="center">
              <Text
                fw={700}
                size="xl"
                mt="sm"
                style={{
                  color: (() => {
                    const g = getGrade(score);
                    if (g === "A") return "var(--mantine-color-green-6)";
                    if (g === "B") return "var(--mantine-color-blue-6)";
                    if (g === "C") return "var(--mantine-color-yellow-6)";
                    if (g === "D") return "var(--mantine-color-orange-6)";
                    if (g === "F") return "var(--mantine-color-red-6)";
                    return "var(--mantine-color-gray-6)";
                  })(),
                  textShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
              >
                Grade: {getGrade(score)}
              </Text>
              <Text size="sm" c="dimmed" ta="center">
                {(() => {
                  const g = getGrade(score);
                  if (g === "A") return "Excellent work!";
                  if (g === "B") return "Great job!";
                  if (g === "C") return "Good effort.";
                  if (g === "D") return "Needs improvement.";
                  if (g === "F") return "Don't give up! Try again.";
                  return null;
                })()}
              </Text>
            </Stack>
            <Stack gap="xs" align="center">
              <Text fw={500} size="lg">
                Your Score
              </Text>
              <Text c="dimmed" size="sm" ta="center">
                You've completed {assessment?.data?.attempts.length || 0} attempts so far.
                {assessment?.data?.attempts.length && assessment.data.attempts.length > 1
                  ? " Keep practicing to improve your score!"
                  : " Ready to try again?"}
              </Text>
            </Stack>

            <div className="flex-row align-center justify-between width-100">
              <Link to={`/d/courses/${assessment?.data?.assessment.courseID}`}>
                <Button
                  w="48%"
                  variant="light"
                  color="gray"
                  size="sm"
                  rightSection={<IconArrowBack size={16} />}
                >
                  Back to Course
                </Button>
              </Link>

              <Button
                w="48%"
                color="green"
                onClick={handleRetake}
                size="sm"
                rightSection={<IconRefresh size={16} />}
              >
                Take Again
              </Button>
            </div>
          </Stack>
        </Box>
      </Modal>

      <Modal
        opened={showExitConfirm}
        onClose={() => setShowExitConfirm(false)}
        centered
        size="md"
        withCloseButton={false}
        padding={0}
        radius="md"
      >
        <Box>
          <Paper
            p="xl"
            radius="md"
            style={{
              background:
                "linear-gradient(135deg, var(--mantine-color-red-6) 0%, var(--mantine-color-orange-6) 100%)",
            }}
          >
            <Stack align="center" gap="xs">
              <IconX
                size={48}
                color="white"
                style={{ filter: "drop-shadow(0 0 8px rgba(255,255,255,0.5))" }}
              />
              <Text fw={700} fz={24} c="white" ta="center">
                Exit Assessment?
              </Text>
              <Text c="white" ta="center" size="sm" opacity={0.9}>
                Your progress will not be saved
              </Text>
            </Stack>
          </Paper>

          <Stack p="xl" gap="xl">
            <Group grow>
              <Paper p="md" radius="md" withBorder>
                <Stack gap="xs" align="center">
                  <Text size="sm" c="dimmed">
                    Questions Answered
                  </Text>
                  <Group gap={4}>
                    <Text fw={700} fz={24} c={Colors.PurplePrimary}>
                      {Object.keys(answers).length}
                    </Text>
                    <Text size="sm" c="dimmed">
                      / {totalQuestions}
                    </Text>
                  </Group>
                </Stack>
              </Paper>

              <Paper p="md" radius="md" withBorder>
                <Stack gap="xs" align="center">
                  <Text size="sm" c="dimmed">
                    Progress
                  </Text>
                  <Text fw={700} fz={24} c={Colors.PurplePrimary}>
                    {Math.round((Object.keys(answers).length / totalQuestions) * 100)}%
                  </Text>
                </Stack>
              </Paper>
            </Group>

            <Alert
              icon={<IconAlertCircle size={16} />}
              title="Warning"
              color="red"
              variant="light"
              radius="md"
            >
              <Text size="sm">
                You have answered {Object.keys(answers).length} out of {totalQuestions} questions.
                {Object.keys(answers).length > 0 ? " This progress will be lost if you exit." : ""}
              </Text>
            </Alert>

            <div className="flex-row align-center justify-between width-100">
              <Button
                w="59%"
                variant="light"
                color="gray"
                onClick={() => setShowExitConfirm(false)}
                size="sm"
                rightSection={<IconArrowBack size={16} />}
              >
                Continue Assessment
              </Button>

              <Button
                w="39%"
                color="red"
                onClick={() => navigate(-1)}
                size="sm"
                rightSection={<IconX size={16} />}
              >
                Exit Anyway
              </Button>
            </div>
          </Stack>
        </Box>
      </Modal>
    </Container>
  );
};

export default SingleAssessment;
